import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select, 
  MenuItem,
  InputLabel,
  FormControl,
  Paper,
  Stack,
  Chip,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FormsBottomNavbar from './FormsBottomNavbar';
import SearchBar from './PostBack';
import { AuthContext } from '../../AuthContext/AuthContext';
import { GOOGLEMAPS_API_KEY } from '../../Api/ApiUrls';
import { BASE_URL } from '../../Api/ApiUrls';
import { checkPropertyUploadPermission } from "./subscriptionCheck";
import SubscriptionModal from "./SubscriptionModal";
import GuestLoginModal from '../../GuestLoginModal';
import SuccessAlert from '../../../Components/Users/Alertpage/SuccessAlert/SuccessAlert';
import FailureAlert from '../../../Components/Users/Alertpage/FailureAlert/FailureAlert';
import {
  GoogleMap,
  useLoadScript,
  Autocomplete
} from "@react-google-maps/api";


import MyLocationIcon from "@mui/icons-material/MyLocation";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import { CircularProgress } from '@mui/material';
import FullScreenLoader from '../../FullScreenLoader';
const GOOGLE_MAPS_API_KEY = GOOGLEMAPS_API_KEY; 

// Styled Components
const FilterContainer = styled(Paper)(({ theme }) => ({
  padding: '16px',
  margin: '8px',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  backgroundColor: '#fff',
  minHeight: "75vh"
}));

const containerStyle = {
  width: "100%",
  height: "300px",
  borderRadius: "12px",
  overflow: "hidden",
};

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: '16px',
  marginBottom: '12px',
  color: '#333'
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#8B4513',
  color: 'white',
  fontWeight: 'bold',
  borderRadius: '8px',
  padding: '8px 16px',
  '&:hover': { backgroundColor: '#654321' },
}));

// Constants
const LOOKING_FOR_OPTIONS = ['JV/JD', 'Build to Suit'];

const PROPERTY_TYPE_OPTIONS = {
  'JV/JD': ['Land', 'Plot'],
  'Build to Suit': ['Land', 'Plot']
};

const BUDGET_RANGES = {
  Purchase: ['No Min', '10L', '20L', '30L', '40L', '50L', '60L', '70L', '80L', '90L', '1CR', '1.25CR', '1.5CR', '1.75CR', '2CR', '2.5CR', '3CR', '3.5 CR', '4CR', '4.5CR', '5CR', '5.5CR', '6 CR', '8CR', '9CR', '10CR', '15CR', '20CR', '25CR', '30CR', '40CR', '50CR', '60CR', '70CR', '80CR', '90CR', '100CR+++'],
  Rent: ['No Min', '1K', '2K', '3K', '4K', '5K', '10K', '15K', '20K', '25K', '30K', '35K', '40K', '50K', '60K', '70K', '80K', '90K', '1L', '1.25L', '1.5 L', '2L', '2.5L', '3L', '3.5L', '4L', '4.5L', '5L', '6L', '7L', '8L', '9L', '10L', '15L', '20L', '25L', '30L', '30+++'],
  Lease: ['No Min', '1L', '2L', '3L', '4L', '5L', '10L', '15L', '20L', '25L', '30L', '35L', '40L', '50L', '60L', '70L', '80L', '90L', '1CR', '1.25CR', '1.5 CR', '2CR', '2.5CR', '3CR', '3.5CR', '4CR', '4.5CR', '5CR', '6CR', '7CR', '8CR', '9CR', '10CR', '15CR', '20CR', '25CR', '30CR', '30+++'],
  'JV/JD': ['No Min', '1L', '2L', '3L', '4L', '5L', '10L', '15L', '20L', '25L', '30L', '32L', '40L', '50L', '60L', '70L', '80L', '90L', '1CR', '1.25CR', '1.5 CR', '2CR', '2.5CR', '3CR', '3.5CR', '4CR', '4.5CR', '5CR', '6CR', '7CR', '8CR', '9CR', '10CR', '15CR', '20CR', '25CR', '30CR', '30+++'],
  'Build to Suit': ['No Min', '1L', '2L', '3L', '4L', '5L', '10L', '11L', '12L', '21L', '22L', '31L', '32L', '40L', '50L', '60L', '70L', '80L', '90L', '1CR', '1.25CR', '1.5 CR', '2CR', '2.5CR', '3CR', '3.5CR', '4CR', '4.5CR', '5CR', '6CR', '7CR', '8CR', '9CR', '10CR', '15CR', '20CR', '25CR', '30CR', '30+++']
};

const INITIAL_FORM_DATA = {
  length: '', width: '', units: 'ft', area: '', no_of_bedrooms: '',
  min_monthly_rent: '', max_monthly_rent: '', min_lease_per_year: '', max_lease_per_year: '',
  min_acres: '', max_acres: '', budget_per_acre: '', ratio: '', floor: '', expected_rental_income: '',
   posted_by: '',
  roadwidth: '',
  facing: '',
  description: ''
};

// Fields that must only accept numeric input (digits + optional decimal point)
const NUMERIC_ONLY_FIELDS = [
  'length', 'width', 'area',
  'min_acres', 'max_acres', 'budget_per_acre',
  'floor', 'roadwidth',
  'min_monthly_rent', 'max_monthly_rent',
  'min_lease_per_year', 'max_lease_per_year',
  'expected_rental_income'
];

// Fields that accept ratio format like 60:40 (digits and colon only)
const RATIO_FIELDS = ['ratio'];

// Helper Functions
const convertBudgetToNumber = (budgetStr) => {
  if (!budgetStr || budgetStr === 'No Min') return 0;
  if (budgetStr.includes('K')) return parseFloat(budgetStr) * 1000;
  if (budgetStr.includes('L')) return parseFloat(budgetStr) * 100000;
  if (budgetStr.includes('CR')) return parseFloat(budgetStr) * 10000000;
  if (budgetStr.includes('+++')) return parseFloat(budgetStr) * 10000000;
  return parseFloat(budgetStr);
};

const centerDefault = { lat: 17.3850, lng: 78.4867 }; 

const BuilderRequest = () => { 
  const navigate = useNavigate();
   const { userId, isGuest } = useContext(AuthContext);
  const [showGuestModal, setShowGuestModal] = useState(false);
  
  const [lookingFor, setLookingFor] = useState('');
const [locationDetails, setLocationDetails] = useState(null);
const [showSuccess, setShowSuccess] = useState(false);
const [showFailure, setShowFailure] = useState(false);
const [alertMessage, setAlertMessage] = useState("");

const [location, setLocation] = useState(centerDefault);
const [address, setAddress] = useState("");
const [usingCurrentLocation, setUsingCurrentLocation] = useState(false);
const [locationConfirmed, setLocationConfirmed] = useState(false);

const autocompleteRef = useRef(null);
const mapRef = useRef(null);

const { isLoaded } = useLoadScript({
  googleMapsApiKey: GOOGLEMAPS_API_KEY,
  libraries: ["places"],
});

  const [propertyType, setPropertyType] = useState('');
  const [options, setOptions] = useState([]);
  const [minBudget, setMinBudget] = useState('');
  const [maxBudget, setMaxBudget] = useState('');
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [postedByOptions, setPostedByOptions] = useState([]);
const [uploadLimits, setUploadLimits] = useState(null);
const [subscriptionModal, setSubscriptionModal] = useState({
  open: false,
  type: null,
});
 const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
  if (isGuest) {
    setShowGuestModal(true);
  }
}, [isGuest]);


useEffect(() => {
  const loadUploadPermissions = async () => {
    const result = await checkPropertyUploadPermission(userId);

     if (!result.allowed) {
      setSubscriptionModal({
        open: true,
        type: result.message?.toLowerCase().includes("subscription")
          ? "NO_SUBSCRIPTION"
          : "LIMIT_EXCEEDED",
      });
      return;
    }

    setUploadLimits(result.availability);

    const options = [];

    if (result.availability.Agent?.allowed) options.push("Agent");
    if (result.availability["Individual Owner/Builder"]?.allowed) {
      options.push("Owner", "Builder");
    }

    setPostedByOptions(options);
  };

  if (userId) loadUploadPermissions();
}, [userId]);


  // Google Maps Script
  useEffect(() => {
    if (!window.google) {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLEMAPS_API_KEY}&libraries=places`;
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const getAvailableMaxBudgets = () => {
    if (!lookingFor || !minBudget) return BUDGET_RANGES[lookingFor] || [];
    
    const allBudgets = BUDGET_RANGES[lookingFor];
    const minIndex = allBudgets.indexOf(minBudget);
    
    if (minIndex === -1) return allBudgets;
    return allBudgets.slice(minIndex + 1);
  };

   const getCurrentLocation = () => {
    if (navigator.geolocation) {
      setUsingCurrentLocation(true);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setLocation(pos);

          try {
            const response = await fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?latlng=${pos.lat},${pos.lng}&key=${GOOGLE_MAPS_API_KEY}`
            );
            const data = await response.json();

            if (data.status == 'OK' && data.results.length > 0) {
              const address = data.results[0].formatted_address;
              setAddress(address);
            } else {
              console.warn('No address found, fallback to coordinates.');
              setAddress(`Lat: ${pos.lat.toFixed(6)}, Lng: ${pos.lng.toFixed(6)}`);
            }
          } catch (error) {
            console.error('Google Maps API error:', error);
            setAddress(`Lat: ${pos.lat.toFixed(6)}, Lng: ${pos.lng.toFixed(6)}`);
          }
        },
        (error) => {
          console.error("Error getting current location:", error);
          alert("Error getting current location. Please enable location services.");
        }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place && place.geometry) {
        setUsingCurrentLocation(false);
        const newLoc = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        setLocation(newLoc);
        setAddress(place.formatted_address);
      }
    }
  };

  const geocodeAddress = async () => {
    if (!address) return;

    if (usingCurrentLocation) {
      return;
    }

    if (window.google && window.google.maps) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status == 'OK' && results[0]) {
          const location = results[0].geometry.location;
          setLocation({
            lat: location.lat(),
            lng: location.lng(),
          });
          setAddress(results[0].formatted_address);
        }
      });
    }
  };

  // ✅ UPDATED: Numeric-only validation applied here
  const handleFormDataChange = (field, value) => {
    if (NUMERIC_ONLY_FIELDS.includes(field)) {
      // Allow empty string or digits with optional single decimal point
      if (value !== '' && !/^\d*\.?\d*$/.test(value)) return;
    }
    if (RATIO_FIELDS.includes(field)) {
      // Allow digits and a single colon e.g. 60:40
      if (value !== '' && !/^\d{0,3}:?\d{0,3}$/.test(value)) return;
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLookingForChange = (value) => {
    setLookingFor(value);
    setPropertyType('');
    setMinBudget('');
    setMaxBudget('');
    setFormData(INITIAL_FORM_DATA);
  };

const validateForm = () => {
  const newErrors = {};
  if (!lookingFor) newErrors.lookingFor = "Please select a type";
  if (!propertyType) newErrors.propertyType = "Please select a property type";
  if (!address || address.trim() === '') newErrors.location = "Please select a location";
  if (!minBudget) newErrors.minBudget = "Please select a min budget";
  if (!maxBudget) newErrors.maxBudget = "Please select a max budget";
  if (!formData.posted_by) newErrors.posted_by = "Please select who is posting";
  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

const handleSave = async () => {
  console.log("📌 Checking subscription before submit...");

  if (!validateForm()) return;

  if (!uploadLimits) {
    setSubscriptionModal({
      open: true,
      type: "NO_SUBSCRIPTION",
    });
    return;
  }

  const roleKey =
    formData.posted_by === "Agent"
      ? "Agent"
      : "Individual Owner/Builder";

  if (!uploadLimits[roleKey]?.allowed) {
    setSubscriptionModal({
      open: true,
      type: "LIMIT_EXCEEDED",
    });
    return;
  }

  const permission = await checkPropertyUploadPermission(userId);

  if (!permission.allowed) {
    alert(permission.message);
    return;
  }

  setLoading(true);

  try {
    const payload = {
      type: lookingFor.toLowerCase(),
      property_type: propertyType,
      length: parseFloat(formData.length) || 0,
      width: parseFloat(formData.width) || 0,
      units: formData.units,
      min_budget: convertBudgetToNumber(minBudget),
      max_budget: convertBudgetToNumber(maxBudget),
      min_acres: parseFloat(formData.min_acres) || 0,
      max_acres: parseFloat(formData.max_acres) || 0,
      ratio: formData.ratio,
      lat: location.lat,
      long: location.lng,
      location: address,
      floor: parseInt(formData.floor) || 0,
      comment: "Property request from form",
      user_id: userId,
      posted_by: formData.posted_by,
      roadwidth: parseFloat(formData.roadwidth) || 0,
      facing: formData.facing,
      description: formData.description,
    };

    const response = await fetch(`${BASE_URL}/property/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      setAlertMessage("Property request saved successfully!");
       setShowSuccess(true);
    } else {
      setAlertMessage("Failed to save property request");
       setShowFailure(true); 
    }
  } catch (error) {
    console.error(error);
    setAlertMessage("Error while saving property");
     setShowFailure(true);
  } finally {
    setLoading(false);
  }
};


  const handleCancel = () => navigate(-1);
  const handleBackClick = () => navigate(-1);

  // Field Components
  const renderDimensionFields = () => (
    <Box key="dimensions" sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Dimensions</Typography>
      <Stack direction="row" spacing={1}>
        <TextField
          fullWidth
          label="Length"
          placeholder="e.g., 40"
          variant="outlined"
          size="small"
          value={formData.length}
          onChange={(e) => handleFormDataChange('length', e.target.value)}
          inputProps={{ inputMode: 'decimal', pattern: '[0-9]*' }}  // ✅ numeric keyboard on mobile
        />
        <Typography sx={{ alignSelf: 'center' }}>×</Typography>
        <TextField
          fullWidth
          label="Width"
          placeholder="e.g., 60"
          variant="outlined"
          size="small"
          value={formData.width}
          onChange={(e) => handleFormDataChange('width', e.target.value)}
          inputProps={{ inputMode: 'decimal', pattern: '[0-9]*' }}  // ✅ numeric keyboard on mobile
        />
        <TextField select label="Units" value={formData.units} size="small" sx={{ minWidth: 80 }}
          onChange={(e) => handleFormDataChange('units', e.target.value)}>
          <MenuItem value="ft">ft</MenuItem>
          <MenuItem value="m">m</MenuItem>
        </TextField>
      </Stack>
    </Box>
  );

  const renderRangeFields = (minField, maxField, label) => (
    <Box key={label} sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>{label}</Typography>
      <Stack direction="row" spacing={1}>
        <TextField
          fullWidth
          label={`Min ${label}`}
          placeholder="e.g., 10"
          variant="outlined"
          size="small"
          value={formData[minField]}
          onChange={(e) => handleFormDataChange(minField, e.target.value)}
          inputProps={{ inputMode: 'decimal', pattern: '[0-9]*' }}  // ✅ numeric keyboard on mobile
        />
        <TextField
          fullWidth
          label={`Max ${label}`}
          placeholder="e.g., 15"
          variant="outlined"
          size="small"
          value={formData[maxField]}
          onChange={(e) => handleFormDataChange(maxField, e.target.value)}
          inputProps={{ inputMode: 'decimal', pattern: '[0-9]*' }}  // ✅ numeric keyboard on mobile
        />
      </Stack>
    </Box>
  );

  const renderBedroomField = () => (
    <FormControl fullWidth key="bedrooms" sx={{ mb: 2 }} size="small">
      <InputLabel>Number of Bedrooms</InputLabel>
      <Select label="Number of Bedrooms" value={formData.no_of_bedrooms}
        onChange={(e) => handleFormDataChange('no_of_bedrooms', e.target.value)}>
        {[1, 2, 3, 4].map(num => <MenuItem key={num} value={num.toString()}>{num} BHK</MenuItem>)}
      </Select>
    </FormControl>
  );

  // ✅ UPDATED renderTextField: applies inputMode for numeric fields
  const renderTextField = (field, label, placeholder) => (
    <TextField
      key={field}
      fullWidth
      label={label}
      placeholder={placeholder}
      variant="outlined"
      size="small"
      sx={{ mb: 2 }}
      value={formData[field]}
      onChange={(e) => handleFormDataChange(field, e.target.value)}
      inputProps={
        NUMERIC_ONLY_FIELDS.includes(field)
          ? { inputMode: 'decimal', pattern: '[0-9]*' }  // ✅ numeric keyboard on mobile
          : {}
      }
    />
  );

  // Dynamic Fields Logic
  const getDynamicFields = () => {
    if (!propertyType || !lookingFor) return null;

    const fields = [];
    const config = {
      Purchase: {
        dimensions: ['Plot', 'Rental with Duplex Building', 'Duplex House', 'Land', 'PG', 'Office', 'Flat', 'Villas', 'Commercial Building', 'Apartment', 'Rental Building'],
        acres: ['Land'],
        budgetPerAcre: ['Land'],
        rentalIncome: ['PG', 'Office', 'Flat', 'Villas', 'Commercial Building', 'Apartment', 'Rental Building'],
        bedrooms: ['Flat', 'Villas']
      },
      Rent: {
        bedrooms: ['House', 'Duplex House', 'Villa'],
        monthlyRent: ['*'],
        dimensions: ['Commercial Plot', 'Commercial Building', 'Commercial Land', 'PG', 'Office', 'Flat'],
        acres: ['Commercial Land'],
        area: ['Office', 'Flat']
      },
      Lease: {
        bedrooms: ['House', 'Duplex House', 'Villa'],
        leasePerYear: ['*'],
        dimensions: ['Plot', 'Commercial Building', 'Commercial Plot', 'Commercial Land', 'PG', 'Office', 'Flat'],
        monthlyRent: ['Commercial Plot', 'Commercial Land'],
        acres: ['Commercial Land'],
        area: ['Office', 'Flat']
      },
      'JV/JD': {
        acres: ['Land'],
        dimensions: ['Plot'],
        ratio: ['*']
      },
      'Build to Suit': {
        acres: ['Land'],
        dimensions: ['Plot'],
        floor: ['Plot']
      }
    };

    const currentConfig = config[lookingFor];
    if (!currentConfig) return fields;

    Object.entries(currentConfig).forEach(([fieldType, propertyTypes]) => {
      if (propertyTypes.includes('*') || propertyTypes.includes(propertyType)) {
        switch (fieldType) {
          case 'dimensions':
            fields.push(renderDimensionFields());
            break;
          case 'acres':
            fields.push(renderRangeFields('min_acres', 'max_acres', 'Acres'));
            break;
          case 'monthlyRent':
            fields.push(renderRangeFields('min_monthly_rent', 'max_monthly_rent', 'Monthly Rent'));
            break;
          case 'leasePerYear':
            fields.push(renderRangeFields('min_lease_per_year', 'max_lease_per_year', 'Lease per Year'));
            break;
          case 'budgetPerAcre':
            fields.push(renderTextField('budget_per_acre', 'Budget per Acre', 'e.g., 500000'));
            break;
          case 'rentalIncome':
            fields.push(renderTextField('expected_rental_income', 'Expected Rental Income', 'e.g., 50000'));
            break;
          case 'bedrooms':
            fields.push(renderBedroomField());
            break;
          case 'ratio':
            fields.push(renderTextField('ratio', 'Ratio', 'e.g., 60:40'));
            break;
          case 'floor':
            fields.push(renderTextField('floor', 'Floor', 'e.g., 3'));
            break;
          case 'area':
            fields.push(renderTextField('area', 'Area (sq ft)', 'e.g., 1200'));
            break;
          default:
            break;
        }
      }
    });

    return fields;
  };

  return (
    <>
      <SearchBar onBackClick={handleBackClick} />
    <Box sx={{
    padding: '16px',
    backgroundColor: '#f5f1eb',
    minHeight: '100vh',
    height: '100%',
    marginTop: '50px',
    paddingBottom: '120px',
    overflowY: 'auto'
  }}>
      <FilterContainer>
        <SectionTitle>Looking For</SectionTitle>
        <FormControl fullWidth sx={{ mb: 2 }} size="small" error={!!errors.lookingFor}>
          <InputLabel>Select Type *</InputLabel>
          <Select value={lookingFor} label="Select Type *" onChange={(e) => { handleLookingForChange(e.target.value); setErrors(prev => ({ ...prev, lookingFor: '' })); }}>
            {LOOKING_FOR_OPTIONS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
          </Select>
          {errors.lookingFor && <Typography sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5, ml: 1.5 }}>{errors.lookingFor}</Typography>}
        </FormControl>

        <SectionTitle>Property Type</SectionTitle>
        <FormControl fullWidth sx={{ mb: 2 }} size="small" error={!!errors.propertyType}>
          <InputLabel>Select Property Type *</InputLabel>
          <Select value={propertyType} label="Select Property Type *" onChange={(e) => { setPropertyType(e.target.value); setErrors(prev => ({ ...prev, propertyType: '' })); }} disabled={!lookingFor}>
            {lookingFor && PROPERTY_TYPE_OPTIONS[lookingFor]?.map((type) => <MenuItem key={type} value={type}>{type}</MenuItem>)}
          </Select>
          {errors.propertyType && <Typography sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5, ml: 1.5 }}>{errors.propertyType}</Typography>}
        </FormControl>

   <Box sx={{ mb: 2 }}>
  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
    Location
  </Typography>

  <Autocomplete
    onLoad={(autocomplete) => {
      autocompleteRef.current = autocomplete;
    }}
    onPlaceChanged={onPlaceChanged}
  >
    <TextField
      fullWidth
      label="Search Location *"
      variant="outlined"
      value={address}
      onChange={(e) => { setAddress(e.target.value); setErrors(prev => ({ ...prev, location: '' })); }}
      error={!!errors.location}
      helperText={errors.location || ''}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={getCurrentLocation} edge="end">
              <MyLocationIcon />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  </Autocomplete>

<div style={{ ...containerStyle, position: 'relative' }}>
  <GoogleMap
    mapContainerStyle={{ width: '100%', height: '100%' }}
    center={location}
    zoom={15}
    options={{ gestureHandling: 'greedy' }}
    onLoad={(map) => {
      mapRef.current = map;
    }}
    onDragStart={() => {
      setLocationConfirmed(false);
    }}
  />

  {/* Fixed pin at center */}
  <div
    style={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -100%)',
      pointerEvents: 'none',
      zIndex: 10,
    }}
  >
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24">
      <path
        fill="#ff0000"
        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
      />
    </svg>
  </div>

  {/* Confirm location button */}
  <button
    type="button"
    onClick={() => {
      if (window.google && window.google.maps && mapRef.current) {
        const center = mapRef.current.getCenter();
        const lat = center.lat();
        const lng = center.lng();
        setLocation({ lat, lng });

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode(
          { location: { lat, lng } },
          (results, status) => {
            if (status === 'OK' && results[0]) {
              const selectedAddress = results[0].formatted_address;
              setAddress(selectedAddress);
              setFormData(prev => ({
                ...prev,
                location: selectedAddress,
                lat: lat.toString(),
                long: lng.toString(),
              }));
            } else {
              const fallback = `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`;
              setAddress(fallback);
              setFormData(prev => ({
                ...prev,
                location: fallback,
                lat: lat.toString(),
                long: lng.toString(),
              }));
            }
            setLocationConfirmed(true);
          }
        );
      }
    }}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: locationConfirmed ? 'green' : 'red',
      border: 'none',
      borderRadius: '50%',
      width: '44px',
      height: '44px',
      cursor: 'pointer',
      boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
      position: 'absolute',
      right: '10px',
      top: '45%',
      transform: 'translateY(-50%)',
      zIndex: 10,
      transition: 'background-color 0.3s ease',
    }}
  >
    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24">
      <path fill="white" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
    </svg>
  </button>
</div>
</Box>
        <SectionTitle sx={{ mt: 2 }}>Budget Range</SectionTitle>
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <FormControl fullWidth size="small" error={!!errors.minBudget}>
              <InputLabel>Min Budget *</InputLabel>
              <Select value={minBudget} label="Min Budget *" onChange={(e) => { setMinBudget(e.target.value); setErrors(prev => ({ ...prev, minBudget: '' })); }} disabled={!lookingFor}>
                {lookingFor && BUDGET_RANGES[lookingFor]?.map((budget, index) => <MenuItem key={index} value={budget}>{budget}</MenuItem>)}
              </Select>
              {errors.minBudget && <Typography sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5, ml: 1.5 }}>{errors.minBudget}</Typography>}
            </FormControl>
            <FormControl fullWidth size="small" error={!!errors.maxBudget}>
              <InputLabel>Max Budget *</InputLabel>
              <Select value={maxBudget} label="Max Budget *" onChange={(e) => { setMaxBudget(e.target.value); setErrors(prev => ({ ...prev, maxBudget: '' })); }} disabled={!lookingFor || !minBudget}>
                {getAvailableMaxBudgets().map((budget, index) => <MenuItem key={index} value={budget}>{budget}</MenuItem>)}
              </Select>
              {errors.maxBudget && <Typography sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5, ml: 1.5 }}>{errors.maxBudget}</Typography>}
            </FormControl>
          </Stack>
        </Box>

        {getDynamicFields()}

<FormControl 
  fullWidth 
  size="small" 
  sx={{ 
    mb: 2,
    '& .MuiInputLabel-root': {
      backgroundColor: 'white',
      px: 0.5,
    },
    '& .MuiSelect-select': {
      py: 1.5, // Ensure consistent padding
    }
  }} 
  error={!!errors.posted_by}
>
  <InputLabel>Posted By *</InputLabel>
  <Select
    value={formData.posted_by}
    onChange={(e) => { 
      handleFormDataChange("posted_by", e.target.value); 
      setErrors(prev => ({ ...prev, posted_by: '' })); 
    }}
    MenuProps={{
      PaperProps: {
        sx: {
          maxHeight: 200, // Limit dropdown height
          width: 'auto',
          minWidth: 150,
        }
      }
    }}
  >
    {postedByOptions.map((role) => (
      <MenuItem key={role} value={role} sx={{ py: 1 }}>
        {role}
      </MenuItem>
    ))}
  </Select>
  {errors.posted_by && (
    <Typography sx={{ 
      color: '#d32f2f', 
      fontSize: '0.75rem', 
      mt: 0.5, 
      ml: 1.5 
    }}>
      {errors.posted_by}
    </Typography>
  )}
</FormControl>



{/* ✅ Road Width — numeric only */}
<TextField
  fullWidth
  size="small"
  label="Road Width (ft)"
  sx={{ mb: 2 }}
  value={formData.roadwidth}
  onChange={(e) => handleFormDataChange('roadwidth', e.target.value)}
  inputProps={{ inputMode: 'decimal', pattern: '[0-9]*' }}
/>

{/* Facing Field */}
<FormControl 
  fullWidth 
  size="small" 
  sx={{ 
    mb: 2,
    '& .MuiInputLabel-root': {
      backgroundColor: 'white',
      px: 0.5,
    },
    '& .MuiSelect-select': {
      py: 1.5,
    }
  }}
>
  <InputLabel>Facing</InputLabel>
  <Select
    value={formData.facing}
    onChange={(e) => handleFormDataChange('facing', e.target.value)}
    MenuProps={{
      PaperProps: {
        sx: {
          maxHeight: 200,
          width: 'auto',
          minWidth: 150,
        }
      }
    }}
  >
    {['East','West','North','South','North-East','North-West','South-East','South-West'].map((v) => (
      <MenuItem key={v} value={v} sx={{ py: 1 }}>
        {v}
      </MenuItem>
    ))}
  </Select>
</FormControl>


        <>
  {/* Full Screen Loader */}
  <FullScreenLoader
    open={loading}
    text="Saving changes..."
  />

  <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
    <Button
      variant="outlined"
      fullWidth
      onClick={handleCancel}
      disabled={loading}
      sx={{
        borderRadius: "8px",
        borderColor: "#8B4513",
        color: "#8B4513",
        "&:hover": {
          borderColor: "#654321",
          backgroundColor: "rgba(139, 69, 19, 0.04)",
        },
      }}
    >
      Cancel
    </Button>

    <StyledButton
      fullWidth
      onClick={handleSave}
      disabled={isGuest || loading}
      sx={{
        backgroundColor: (isGuest || loading) ? "#ccc" : undefined,
        color: (isGuest || loading) ? "#777" : undefined,
        cursor: (isGuest || loading) ? "not-allowed" : "pointer",
        "&:hover": {
          backgroundColor: (isGuest || loading) ? "#ccc" : undefined,
        },
      }}
    >
      {isGuest ? "Login Required" : "Save"}
    </StyledButton>
  </Stack>
</>
      </FilterContainer>
    </Box>
    <SubscriptionModal
  open={subscriptionModal.open}
  type={subscriptionModal.type}
  onClose={() =>
    setSubscriptionModal({ open: false, type: null })
  }
/>


    <GuestLoginModal
  open={showGuestModal}
  onClose={() => {
    setShowGuestModal(false);
  }}
/>
  {showSuccess && (
              <SuccessAlert
                message={alertMessage}
                onClose={() => {
                  setShowSuccess(false);
                  navigate("/dashboard");
                }}
              />
            )}
            
            {showFailure && (
              <FailureAlert
                message={alertMessage}
                onClose={() => {
                  console.log("Closing failure alert");
                  setShowFailure(false);
                  setAlertMessage(""); 
                  navigate("/builder-registration-form");
                }}
              />
            )}
    <FormsBottomNavbar />
    </>
  );
};

export default BuilderRequest;